import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CoachNow } from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fetchCoachNow } from "@/lib/coach-now-api";
import {
  coachChangeLines,
  coachToDoLines,
  fmtDay,
  latestCheckinLine,
  latestContactLine,
  nextCheckinLine,
} from "@/lib/coach-now";

// TES-1074 point 5 — the top of "Aktuellt": what changed, what to do now,
// the latest and next check-in, open follow-ups and agreements, the latest
// contact. The neutral medical status, when a case is open, sits in the top
// summary right above (TES-1101). The chat and the check-in list follow below it, unchanged.
//
// `refreshKey` changes when the page reloads its own data (a check-in was
// handled), so the counts follow.
export function CoachNowPanel({
  patientId,
  refreshKey,
  onUnauthorized,
}: {
  patientId: string;
  refreshKey: number;
  onUnauthorized?: () => void;
}) {
  const [now, setNow] = useState<CoachNow | null>(null);
  const [error, setError] = useState<string | null>(null);
  // In a ref: the page passes this inline, and a new identity per render
  // would refetch on every render.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchCoachNow(patientId)
      .then((n) => {
        if (!cancelled) setNow(n);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current?.();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, refreshKey]);

  return (
    <section
      aria-labelledby="aktuellt-nu"
      className="rounded-md border border-surfaceMuted bg-surface p-md flex flex-col gap-md"
    >
      <h2 id="aktuellt-nu" className="font-display text-lg">
        Just nu
      </h2>
      {error ? (
        <p className="text-sm text-danger">Kunde inte hämta: {error}</p>
      ) : !now ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : (
        <>
          {/* TES-1101 — the neutral medical status is shown once, in the
              top summary ("Översikt") directly above this level, rather than
              twice a few lines apart. */}
          <div className="grid gap-md md:grid-cols-2">
            <Block title="Att göra nu">
              <ul className="list-disc pl-lg text-sm">
                {coachToDoLines(now).map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </Block>
            <Block title="Förändringar">
              {coachChangeLines(now).map((l) => (
                <p key={l} className="text-sm">
                  {l}
                </p>
              ))}
            </Block>
            <Block title="Incheckningar">
              <p className="text-sm">Senaste: {latestCheckinLine(now)}</p>
              <p className="text-sm">Nästa: {nextCheckinLine(now)}</p>
            </Block>
            <Block title="Senaste kontakt">
              <p className="text-sm">{latestContactLine(now)}</p>
            </Block>
            <Block title="Öppna uppföljningar och överenskommelser">
              {now.followUps.length === 0 && now.agreements.length === 0 ? (
                <p className="text-sm text-textSecondary">
                  Inga öppna uppföljningar eller överenskommelser.
                </p>
              ) : (
                <ul className="flex flex-col gap-xs text-sm">
                  {now.followUps.map((f) => (
                    <li key={f.id}>
                      <span className="text-textPrimary">{f.prompt}</span>
                      <span className="block text-xs text-textSecondary">
                        Uppföljning {fmtDay(f.dueAt)}
                        {f.status === "due" ? " · ska göras nu" : ""}
                      </span>
                    </li>
                  ))}
                  {now.agreements.map((a) => (
                    <li key={a.itemId}>
                      <span className="text-textPrimary">{a.body}</span>
                      <span className="block text-xs text-textSecondary">
                        Överenskommelse {fmtDay(a.observedAt)} · Patientminnet
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          </div>
        </>
      )}
    </section>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-xs text-xs font-semibold uppercase tracking-wide text-textSecondary">
        {title}
      </h3>
      {children}
    </div>
  );
}
